import React, { useState, useEffect, useContext, useRef } from 'react';
import { FaRegEdit } from 'react-icons/fa';
import PropTypes from 'prop-types';
import cn from 'src/taskpane/utility/bem';
import { TextField } from '@fluentui/react';
import { CmicContext } from 'src/taskpane/context/CmicContext';
import { handleTextTheme } from 'src/taskpane/shared/styles';
import Button from 'src/taskpane/shared/Button';
import ActionImage from 'src/taskpane/shared/ActionImage';
import './styles.scss';


const className = cn('basic-auth-login');
const el = (name, mod) => cn(className, name, mod);


const BasicAuthLogin = ({
    handleOnFocus,
    handleOnUsernameBlur,
    handleOnPasswordBlur,
    handlePasswordChange,
    handleUsernameChange,
    handleBasicAuthLoginSubmit,
    isCapsLockOn,
    isPasswordValid,
    isUsernameValid,
    passwordError,
    passwordLabel,
    passwordVal,
    setIsCapsLockOn,
    textInputStyles,
    usernameError,
    usernameLabel,
    usernameVal,
    handleBack,
    actionChoice,
    URLError,
    URLVal,
    URLLabel,
    handleURLValueChange,
    handleOnURLBlur,
    isURLValid,
}) => {
    const [editMode, setEditMode] = useState(!URLVal || false);
    const { currentOfficeTheme } = useContext(CmicContext);
    const text_theme = handleTextTheme({ themeId: currentOfficeTheme.themeId });

    const envUrlInputRef = useRef(null);
    const envUrlLabelRef = useRef(null);

    // Focus the input when entering edit mode
    useEffect(() => {
        if (editMode && envUrlInputRef.current) {
            envUrlInputRef.current.focus();
        }
    }, [editMode]);

    // Allow horizontal scrolling for the URL label
    useEffect(() => {
        const el = envUrlLabelRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (e.deltaY !== 0) {
                e.preventDefault();
                el.scrollLeft -= e.deltaY;
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const renderEnvironmentURLField = () => {
        if (editMode) {
            return <TextField
                id="EnvironmentURL"
                componentRef={envUrlInputRef}
                className={`${el('input')} ${text_theme}`}
                label={URLLabel}
                value={URLVal}
                placeholder={URLLabel}
                onChange={handleURLValueChange}
                onBlur={handleOnURLBlur}
                onFocus={handleOnFocus}
                errorMessage={URLError}
                invalid={!isURLValid}
                styles={textInputStyles}
                aria-label={URLLabel}
                autoComplete="off"
                aria-required
            />;
        } else {
            return (
                <div className={el('envurl-container')}>
                    <div className={el('envurl-label')}>
                        {URLLabel}
                    </div>
                    <div className={el('envurl-value-container')}>
                        <span className={el('envurl-value')} ref={envUrlLabelRef}>{URLVal}</span>
                        <button
                            className={el('edit-button')}
                            type="button"
                            aria-label="Edit URL"
                            onClick={() => setEditMode(true)}
                        >
                            <FaRegEdit size={18} />
                        </button>
                    </div>
                </div >
            );
        }
    };

    return (
        <div className={className}>
            <div className={el('content')}>
                <ActionImage actionType={actionChoice} fill center />
                <div className={el('input-container')}>
                    {renderEnvironmentURLField()}
                    <TextField
                        label={usernameLabel}
                        className={`${el('input')} ${text_theme}`}
                        value={usernameVal}
                        placeholder={`Enter ${usernameLabel}`}
                        onChange={handleUsernameChange}
                        onBlur={handleOnUsernameBlur}
                        onFocus={handleOnFocus}
                        errorMessage={usernameError}
                        invalid={!isUsernameValid}
                        styles={textInputStyles}
                        aria-label={usernameLabel}
                        autoComplete="off"
                        aria-required
                    />
                    <TextField
                        label={passwordLabel}
                        className={`${el('input')} ${text_theme}`}
                        value={passwordVal}
                        placeholder={`Enter ${passwordLabel}`}
                        onChange={handlePasswordChange}
                        onBlur={handleOnPasswordBlur}
                        onFocus={handleOnFocus}
                        errorMessage={
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{passwordError}</span>
                                {isCapsLockOn && <span>Caps Lock is On</span>}
                            </div>
                        }
                        invalid={!isPasswordValid}
                        onKeyUp={e => {
                            setIsCapsLockOn(e.getModifierState('CapsLock'));
                        }}
                        type="password"
                        revealPasswordAriaLabel="Show password"
                        styles={textInputStyles}
                        aria-label={passwordLabel}
                        autoComplete="off"
                        canRevealPassword
                        aria-required
                    />
                </div>

                <Button text="Sign In" onClick={handleBasicAuthLoginSubmit} />
            </div>
            <div className={el('footer')}>
                <Button
                    customClasses={el('button')}
                    variant="secondary"
                    text="Back"
                    onClick={handleBack}
                />
            </div>
        </div>
    );
};

export default BasicAuthLogin;

BasicAuthLogin.propTypes = {
    handleOnFocus: PropTypes.func.isRequired,
    handleOnURLBlur: PropTypes.func.isRequired,
    handleOnUsernameBlur: PropTypes.func.isRequired,
    handleOnPasswordBlur: PropTypes.func.isRequired,
    handleURLValueChange: PropTypes.func.isRequired,
    handlePasswordChange: PropTypes.func.isRequired,
    handleUsernameChange: PropTypes.func.isRequired,
    handleBasicAuthLoginSubmit: PropTypes.func.isRequired,
    handleBack: PropTypes.func.isRequired,
    isCapsLockOn: PropTypes.bool.isRequired,
    isURLValid: PropTypes.bool.isRequired,
    isPasswordValid: PropTypes.bool.isRequired,
    isUsernameValid: PropTypes.bool.isRequired,
    URLError: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node
    ]),
    URLLabel: PropTypes.string.isRequired,
    URLVal: PropTypes.string,
    passwordError: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node
    ]),
    passwordLabel: PropTypes.string.isRequired,
    passwordVal: PropTypes.string,
    setIsCapsLockOn: PropTypes.func.isRequired,
    textInputStyles: PropTypes.func,
    usernameError: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node
    ]),
    usernameLabel: PropTypes.string.isRequired,
    usernameVal: PropTypes.string,
    actionChoice: PropTypes.string.isRequired,
};